import Analytics from '@web/services/Analytics/Analytics'

/// События для главной страницы

/**
 * Событие открытия главной страницы
 * @param userId - ID пользователя
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'main_open', user_id, event_date
 */
export function analyticsEventMainOpen (userId: string, platform = 'test_lk'): void {
  Analytics.send({
    action: 'main_open',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'При загрузке главной страницы',
    target: 'Отслеживание входов',
    platform
  })
}

/**
 * Событие показа баннера на главной странице
 * @param userId - ID пользователя
 * @param bannerId - ID баннера
 * @param position - позиция баннера
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'banner_view', user_id, banner_id, position, event_date
 */
export function analyticsEventBannerView ({ userId, bannerId, position, promo, platform = 'test_lk' }: { userId: string, bannerId: string, position: string, promo: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'banner_view',
    user_id: userId,
    banner_id: bannerId,
    position,
    event_date: new Date().toISOString(),
    when_trigger: 'Когда показывается баннер',
    target: 'Отслеживание показов баннеров',
    utm_source: 'lk',
    utm_medium: 'banner',
    utm_campaign: position,
    utm_term: bannerId,
    promo,
    platform
  })
}

/**
 * Событие клика по баннеру на главной странице
 * @param userId - ID пользователя
 * @param bannerId - ID баннера
 * @param position - позиция баннера
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'banner_click', user_id, banner_id, position, event_date
 */
export function analyticsEventBannerClick ({ userId, bannerId, position, promo, platform = 'test_lk' }: { userId: string, bannerId: string, position: string, promo: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'banner_click',
    user_id: userId,
    banner_id: bannerId,
    position,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по баннеру',
    target: 'Переходы по баннерам',
    utm_source: 'lk',
    utm_medium: 'banner',
    utm_campaign: position,
    utm_term: bannerId,
    promo,
    platform
  })
}

/**
 * Событие клика по категории из блока "Больше категорий"
 * @param userId - ID пользователя
 * @param categoryName - название категории
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'category_click', user_id, category_name, event_date
 */
export function analyticsEventCategoryClick ({ userId, categoryName, platform = 'test_lk' }: { userId: string, categoryName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'category_click',
    user_id: userId,
    category_name: categoryName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по тегу из блока “Больше категорий”',
    target: 'Интересы пользователя',
    platform
  })
}

/**
 * Событие просмотра карточки курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param categoryName - название категории
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_card_view', user_id, course_page_id, category_name, event_date
 */
export function analyticsEventCourseCardView ({ userId, coursePageId, categoryName, platform = 'test_lk' }: { userId: string, coursePageId: string, categoryName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'course_card_view',
    user_id: userId,
    course_page_id: coursePageId,
    category_name: categoryName,
    event_date: new Date().toISOString(),
    when_trigger: 'Видимость карточек определенной категории',
    target: 'Вовлеченность в контент',
    platform
  })
}

/**
 * Событие клика по карточке курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param categoryName - название категории
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_card_click', user_id, course_page_id, category_name, event_date
 */
export function analyticsEventCourseCardClick ({ userId, coursePageId, categoryName, platform = 'test_lk' }: { userId: string, coursePageId: string, categoryName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'course_card_click',
    user_id: userId,
    course_page_id: coursePageId,
    category_name: categoryName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по карточке курса',
    target: 'Интересы пользователя',
    platform
  })
}

/**
 * Событие глубины прокрутки главной страницы
 * @param userId - ID пользователя
 * @param scrollPercent - процент прокрутки ('25', '50', '75', '100')
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'scroll_depth', user_id, scroll_percent, event_date
 */
export function analyticsEventScrollDepth ({ userId, scrollPercent, platform = 'test_lk' }: { userId: string, scrollPercent: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'scroll_depth',
    user_id: userId,
    scroll_percent: scrollPercent,
    event_date: new Date().toISOString(),
    when_trigger: 'Пользователь проскроллил страницу (25/50/75/100%)',
    target: 'Измерение вовлечённости',
    platform
  })
}

/// События для страницы курса

/**
 * Событие открытия промо-лендинга курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_page_open', user_id, course_page_id, event_date
 */
export function analyticsEventCoursePageOpen ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'course_page_open',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'При открытии промо лендинга курса (когда кликают на карточку курса с главной)',
    target: 'Построение воронки',
    platform
  })
}

/**
 * Событие клика по баннеру "Попробовать бесплатно"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'banner_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventBannerCtaTryFreeClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'banner_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по баннеру "Попробовать бесплатно"',
    target: 'переходы по баннерам',
    platform
  })
}

/**
 * Событие клика по закрепленной кнопке "Попробовать бесплатно"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'fix_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixCtaTryFreeClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'fix_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на закрепе по кнопке "попробовать бесплатно"',
    target: 'Переход в пэйвол',
    platform
  })
}

/**
 * Событие клика по зафиксированной кнопке внизу страницы в мобильной версии
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'fix_mobile_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixMobileCtaTryFreeClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'fix_mobile_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по зафиксированной кнопке внизу страницы в мобильной версии',
    target: 'Переход в пэйвол',
    platform
  })
}

/**
 * Событие клика по закрепленной кнопке "Получить доступ"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'fix_cta_buy_course_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixCtaBuyCourseClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'fix_cta_buy_course_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на закрепе по кнопке "получить доступ"',
    target: 'Интерес к покупке курса',
    platform
  })
}

/**
 * Событие клика по кнопке "Назад" на странице курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'back_to_main_click', user_id, course_page_id, event_date
 */
export function analyticsEventBackToMainClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'back_to_main_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке назад',
    target: 'Навигация',
    platform
  })
}

/// События для пэйволла

/**
 * Событие прокрутки пэйволла
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param scrollPercent - процент прокрутки
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'paywall_scroll_depth', user_id, course_page_id, scroll_percent, event_date
 */
export function analyticsEventPaywallScrollDepth ({ userId, coursePageId, scrollPercent, platform = 'test_lk' }: { userId: string, scrollPercent: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'paywall_scroll_depth',
    user_id: userId,
    course_page_id: coursePageId,
    scroll_percent: scrollPercent,
    event_date: new Date().toISOString(),
    when_trigger: 'Прокрутка пэйвола',
    target: 'Насколько изучают экран',
    platform
  })
}

/**
 * Событие клика по кнопке "Начать пробный период" на пэйволле
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'paywall_cta_click', user_id, course_page_id, event_date
 */
export function analyticsEventPaywallCtaClick ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'paywall_cta_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик “Начать пробный период”',
    target: 'Конверсия в переход на форму оплаты',
    platform
  })
}

/**
 * Событие закрытия пэйволла
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'paywall_close', user_id, course_page_id, event_date
 */
export function analyticsEventPaywallClose ({ userId, coursePageId, platform = 'test_lk' }: { userId: string, coursePageId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'paywall_close',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на крестик закрытия пэйвола',
    target: 'Навигация',
    platform
  })
}

/// События для модальных окон

/**
 * Событие показа модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'modal_view', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalView ({ userId, coursePageId = 'home', modalName = 'quiz', platform = 'test_lk' }: { userId: string, coursePageId?: string, modalName?: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'modal_view',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Показ модального окна',
    target: 'Отслеживание количества показов',
    platform
  })
}

/**
 * Событие клика по модальному окну
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'modal_click', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalClick ({ userId, coursePageId = 'home', modalName = 'quiz', platform = 'test_lk' }: { userId: string, coursePageId?: string, modalName?: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'modal_click',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по нему',
    target: 'Конверсия в переход',
    platform
  })
}

/**
 * Событие закрытия модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'modal_close', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalClose ({ userId, coursePageId = 'home', modalName = 'quiz', platform = 'test_lk' }: { userId: string, coursePageId?: string, modalName?: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'modal_close',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Закрытие окна',
    target: 'Конверсия в закрытие',
    platform
  })
}

/**
 * Событие закрытия модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'modal_qwiz_close', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalQwizClose ({ userId, coursePageId = 'home', modalName = 'quiz', platform = 'test_lk' }: { userId: string, coursePageId?: string, modalName?: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'modal_qwiz_close',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Закрытие окна',
    target: 'Конверсия в закрытие',
    platform
  })
}

/**
 * Событие выбора "Больше не показывать" для модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'modal_dont_ask_again', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalNeverShow ({ userId, coursePageId = 'home', modalName = 'quiz', platform = 'test_lk' }: { userId: string, coursePageId?: string, modalName?: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'modal_never_show',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Выбор "Больше не показывать"',
    target: 'Отслеживание отказов от показа',
    platform
  })
}

/// События для коротких курсов

/**
 * Событие нажатия на кнопку "Добавить в мои курсы"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'add_course_SS', user_id, course_id, course_name, event_date
 */
export function analyticsEventAddCourseSS ({ userId, courseId, courseName, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'add_course_SS',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на "Добавить в мои курсы"',
    target: 'Отслеживание добавления курсов',
    platform
  })
}

/**
 * Событие успешного добавления курса
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_added_SS', user_id, course_id, course_name, event_date
 */
export function analyticsEventCourseAddedSS ({ userId, courseId, courseName, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'course_added_SS',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Когда курс был добавлен',
    target: 'Подтверждение добавления курса',
    platform
  })
}

/**
 * Событие нажатия на кнопку "Перейти в курс"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'add_course_SS_go', user_id, course_id, course_name, event_date
 */
export function analyticsEventAddCourseSSGo ({ userId, courseId, courseName, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'add_course_SS_go',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на кнопку "Перейти в курс"',
    target: 'Переход в курс',
    platform
  })
}

/**
 * Событие клик по карточке курса или кнопке, ведущей на курс с главного экрана ЛК (включая burger/main_screen/show_all)
 * @param userId - ID пользователя
 * @param clubId - ID клуба
 * @param clubName - название клуба
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'main_screen_my_courses', user_id, course_id, course_name, event_date, place_button
 */
export function analyticsMainScreenMyCourses ({
  userId,
  courseId,
  courseName,
  placeButton = 'main_screen',
  platform = 'test_lk'
}: {
  userId: string,
  courseId: string;
  courseName: string;
  placeButton: 'main_screen' | 'show_all',
  platform?: AnalyticsPlatformType
}): void {
  Analytics.send({
    action: 'main_screen_my_courses',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    place_button: placeButton,
    event_date: new Date().toISOString(),
    when_trigger: 'клик по карточке курса или кнопке, ведущей на курс с главного экрана ЛК',
    target: 'Переход в курс',
    platform
  })
}

/**
 * Событие клика по кнопке перехода в клуб с главного экрана
 * @param userId - ID пользователя
 * @param clubId - ID клуба
 * @param clubName - название клуба
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'main_screen_club_button', user_id, club_id, club_name, event_date
 */
export function analyticsEventClubButtonClick ({ userId, clubId, clubName, platform = 'test_lk' }: { userId: string, clubId: string, clubName: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'main_screen_club_button',
    user_id: userId,
    club_id: clubId,
    club_name: clubName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке/ссылке перехода в клуб с главного экрана',
    target: 'Переход в клуб',
    platform
  })
}

/**
 * Событие показа страницы домашки
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'homework_screen', user_id, course_id, course_name, event_date
 */
export function analyticsEventShowHomeworkPage ({ userId, courseId, courseName, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, platform?: AnalyticsPlatformType }): void {
  const params = {
    action: 'homework_screen',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Показ страницы домашки',
    target: 'Показ страницы домашки',
    platform,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие нажатия на кнопку "Перейти в курс"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param homeworkId - ID домашки
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'choose_homework', user_id, course_id, course_name, homework_id, event_date
 */
export function analyticsEventChooseHomework ({ userId, courseId, courseName, homeworkId, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, homeworkId: string, platform?: AnalyticsPlatformType }): void {
  const params = {
    action: 'choose_homework',
    user_id: userId,
    homework_id: homeworkId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по домашке',
    target: 'Клик по домашке',
    platform,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие отправки домашки
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param homeworkId - ID домашки
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'homework_done', user_id, course_id, course_name, homework_id, event_date
 */
export function analyticsEventSendHomework ({ userId, courseId, courseName, homeworkId, platform = 'test_lk' }: { userId: string, courseId: string, courseName: string, homeworkId: string, platform?: AnalyticsPlatformType }): void {
  const params = {
    action: 'homework_done',
    user_id: userId,
    homework_id: homeworkId,
    event_date: new Date().toISOString(),
    when_trigger: 'Отправка домашки',
    target: 'Отправка домашки',
    platform,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие клика по пункту меню "Видеоконсультации"
 * @param userId - ID пользователя
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'burger_video', user_id, event_date
 */
export function analyticsEventVideoconsultationsMenuItemClick ({ userId, platform = 'test_lk' }: { userId: string, platform?: AnalyticsPlatformType }): void {
  const params = {
    action: 'burger_video',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Видеоконсультации"',
    target: 'Клик по пункту меню "Видеоконсультации"',
    place_button: 'burger',
    platform
  }
  Analytics.send(params)
}

/**
 * Событие клика по кнопке "Купить консультацию"
 * @param userId - ID пользователя
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'buy_video_consultation', user_id, event_date
 */
export function analyticsEventBuyVideoConsultation ({ userId, platform = 'test_lk', courseId, purchaseId }: { userId: string, platform?: AnalyticsPlatformType, courseId: string, purchaseId: string }): void {
  Analytics.send({
    action: 'buy_video_consultation',
    user_id: userId,
    course_id: courseId,
    purchase_id: purchaseId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке "Купить консультацию"',
    target: 'Клик по кнопке "Купить консультацию"',
    platform
  })
}

/**
 * Событие клика по кнопке "Поддержка"
 * @param userId - ID пользователя
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'support_button', user_id, event_date
 */
export function analyticsEventCareServiceButtonClick ({ userId, platform = 'test_lk' }: { userId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'support_button',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Поддержка"',
    target: 'Клик по пункту меню "Поддержка"',
    platform
  })
}

/**
 * Событие клика по пункту меню "Подписка"
 * @param userId - ID пользователя
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'menu_subscription', user_id, event_date
 */
export function analyticsEventMenuSubscription ({ userId, platform = 'test_lk' }: { userId: string, platform?: AnalyticsPlatformType }): void {
  Analytics.send({
    action: 'menu_subscription',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Подписка"',
    target: 'Клик по пункту меню "Подписка"',
    place_button: 'burger',
    platform
  })
}

/**
 * Событие открытия экрана курса
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_menu', user_id, course_id, course_name, event_date
 */
export function analyticsEventOpenCoursePage ({ userId, platform = 'test_lk', courseId, courseName }: { userId: string, platform?: AnalyticsPlatformType, courseId: string, courseName: string }): void {
  Analytics.send({
    action: 'course_menu',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Открытие экрана курса',
    target: 'Открытие экрана курса',
    platform
  })
}

/**
 * Событие первого нажатия на день в конкретном курсе
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'first_day_tap', user_id, course_id, event_date
 */
export function analyticsEventFirstTimeDayOpen ({ userId, platform = 'test_lk', courseId }: { userId: string, platform?: AnalyticsPlatformType, courseId: string }): void {
  Analytics.send({
    action: 'first_day_tap',
    user_id: userId,
    course_id: courseId,
    event_date: new Date().toISOString(),
    when_trigger: 'Первое в жизни пользователя нажатие на любой день в конкретном курсе',
    target: 'Первое в жизни пользователя нажатие на любой день в конкретном курсе',
    platform
  })
}

/**
 * Событие нажатия на день в конкретном курсе
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'day_tap', user_id, course_id, event_date
 */
export function analyticsEventDayTap ({ userId, platform = 'test_lk', courseId, courseName, dayNumber, dayLessonId }: { userId: string, platform?: AnalyticsPlatformType, courseId: string, courseName: string, dayNumber: string, dayLessonId: string }): void {
  Analytics.send({
    action: 'day_tap',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    day_number: dayNumber,
    day_lesson_id: dayLessonId,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на день в конкретном курсе',
    target: 'Нажатие на день в конкретном курсе',
    platform
  })
}

/**
 * Событие нажатия на день в конкретном курсе
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param periodTariff - период тарифа
 * @param platform - платформа (test_lk, web)
 * Отправляет: action: 'course_progress', user_id, course_id, event_date
 */
export function analyticsEventCourseProgress ({ userId, platform = 'test_lk', courseId, courseName, periodTariff }: { userId: string, platform?: AnalyticsPlatformType, courseId: string, courseName: string, periodTariff: string }): void {
  Analytics.send({
    action: 'course_progress',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    period_tariff: periodTariff,
    event_date: new Date().toISOString(),
    when_trigger: 'Получение обновленного процента завершенности курса (после загрузки прогресса или его пересчета)',
    target: 'Получение обновленного процента завершенности курса (после загрузки прогресса или его пересчета)',
    platform
  })
}
